/**
 * Utilidades compartidas para las impresiones HTML (iframe/ventana +
 * window.print) de los distintos módulos.
 */

/**
 * Encabezado de la empresa para los comprobantes impresos.
 *
 * Acepta el objeto completo `{nombre, direccion, rnc, telefono}` que devuelven
 * los backends nuevos o el string con solo el nombre de los backends viejos /
 * localStorage, para que todos los recibos puedan homologarse sin romper a los
 * llamadores existentes.
 */
export function encabezadoEmpresaHtml(compania) {
  const c = typeof compania === "string" ? { nombre: compania } : compania || {};
  const fiscal = [c.rnc ? `RNC: ${c.rnc}` : "", c.telefono ? `Tel: ${c.telefono}` : ""]
    .filter(Boolean)
    .join(" · ");
  return `
      <h3 style="text-align:center;margin:0 0 2px">${c.nombre || ""}</h3>
      ${c.direccion ? `<p style="text-align:center;margin:0">${c.direccion}</p>` : ""}
      ${fiscal ? `<p style="text-align:center;margin:0 0 10px">${fiscal}</p>` : ""}`;
}

/**
 * Imprime un documento HTML con un iframe oculto (un window.open tras un
 * await queda fuera del gesto del usuario y el navegador lo bloquea).
 */
export function imprimirHtml(html) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

const NAVY = "#18586b";

const escHtml = (s) =>
  String(s ?? "").replace(/[<>&]/g, (ch) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[ch]));

// El contexto arranca con nombreNegocio = "false" (string, no booleano) antes
// del login, y varias pantallas lo pasan tal cual: sin filtrarlo el membrete
// del reporte sale con la palabra "false".
const nombreUtil = (v) => {
  const s = String(v ?? "").trim();
  return s && s !== "false" && s !== "null" && s !== "undefined" ? s : "";
};

// Resuelve el membrete: objeto completo, solo el nombre, o lo que haya
// quedado en el login.
function companiaListado(compania) {
  const comp =
    compania && typeof compania === "object" ? { ...compania } : { nombre: nombreUtil(compania) };
  comp.nombre = nombreUtil(comp.nombre) || nombreUtil(localStorage.getItem("nombrenegocio"));
  return comp;
}

// Hoja de estilos común de los listados impresos (carta, cabecera navy,
// filas alternas, fila de totales destacada).
function estilosListado(pagina = "letter") {
  return `
      @page { size: ${pagina}; margin: 14mm; }
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 190px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .sub { margin: 10px 0 4px; color: #4a5866; font-size: 11px; }
      table.lineas { width: 100%; border-collapse: collapse; margin-top: 4px; }
      table.lineas th { background: ${NAVY}; color: #fff; padding: 6px 8px; font-size: 11px; text-align: left; }
      table.lineas td { padding: 5px 8px; border-bottom: 1px solid #e3e9ee; font-size: 11px; }
      table.lineas tr:nth-child(even) td { background: #f5f8fa; }
      th.num, td.num { text-align: right; }
      tr.tot td { font-weight: bold; border-top: 2px solid ${NAVY}; border-bottom: none; background: #f0f5f8 !important; color: ${NAVY}; }`;
}

// Bloque superior: membrete a la izquierda, datos del documento a la derecha.
function cabeceraListado(comp, titulo, subtitulo, registros) {
  const hoy = new Date();
  return `
      <div class="cab">
        <div class="emp">
          <h1>${escHtml(comp.nombre)}</h1>
          ${comp.direccion ? `<p>${escHtml(comp.direccion)}</p>` : ""}
          <p>${[comp.rnc ? `RNC: ${escHtml(comp.rnc)}` : "", comp.telefono ? `Tel: ${escHtml(comp.telefono)}` : ""]
            .filter(Boolean)
            .join(" · ")}</p>
        </div>
        <div class="doc">
          <div class="titulo">${escHtml(titulo)}</div>
          <p>Emitido: <b>${hoy.toLocaleDateString("es-DO")} ${hoy.toLocaleTimeString("es-DO")}</b></p>
          <p>${registros} registros</p>
        </div>
      </div>
      ${subtitulo ? `<p class="sub">${escHtml(subtitulo)}</p>` : ""}`;
}

/**
 * Listado imprimible en hoja carta con el mismo formato de la factura de
 * venta x mayor (impresionxmayor.js): membrete de la empresa a la izquierda,
 * bloque del documento a la derecha, tabla con cabecera navy y filas
 * alternas, y fila de totales destacada.
 *
 * @param {Object} cfg
 * @param {Object|string} [cfg.compania] {nombre, direccion, rnc, telefono} o el
 *   nombre solo; si falta, usa el nombre del negocio guardado en el login.
 * @param {string}  cfg.titulo       Título del reporte.
 * @param {string}  [cfg.subtitulo]  Rango/filtros (se muestra bajo el título y
 *   en el bloque derecho como fecha de emisión).
 * @param {Array}   cfg.cabeceras    Encabezados de columnas.
 * @param {Array}   cfg.filas        Filas: arrays de celdas ya formateadas.
 * @param {Array}   [cfg.totales]    Fila de totales (mismo largo que cabeceras).
 * @param {Array}   [cfg.totalesextra] Filas de total adicionales, debajo de la
 *   anterior y con el mismo formato: para reportes que además de un total
 *   general necesitan desglosarlo (saldo por moneda de emisión, por ejemplo).
 * @param {Array}   [cfg.numericas]  Índices de columnas alineadas a la derecha.
 */
export function imprimirListadoGrande({
  compania,
  titulo,
  subtitulo,
  cabeceras,
  filas,
  totales,
  totalesextra = [],
  numericas = [],
  // "letter landscape" para listados anchos (muchas columnas).
  pagina = "letter",
}) {
  const comp = companiaListado(compania);
  const num = new Set(numericas);
  const th = cabeceras
    .map((c, i) => `<th${num.has(i) ? ' class="num"' : ""}>${escHtml(c)}</th>`)
    .join("");
  const tr = filas
    .map(
      (f) =>
        `<tr>${f.map((v, i) => `<td${num.has(i) ? ' class="num"' : ""}>${escHtml(v)}</td>`).join("")}</tr>`
    )
    .join("");
  const filaTotal = (f) =>
    `<tr class="tot">${f
      .map((v, i) => `<td${num.has(i) ? ' class="num"' : ""}>${escHtml(v)}</td>`)
      .join("")}</tr>`;
  const tot =
    (totales ? filaTotal(totales) : "") + (totalesextra || []).map(filaTotal).join("");
  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>${escHtml(titulo)}</title>
    <style>${estilosListado(pagina)}
    </style></head>
    <body>
      ${cabeceraListado(comp, titulo, subtitulo, filas.length)}
      <table class="lineas">
        <tr>${th}</tr>
        ${tr}${tot}
      </table>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

/**
 * Listado impreso partido en grupos, con subtotal por grupo y total general.
 * Mismo formato que `imprimirListadoGrande`, para reportes cuya lectura es por
 * categoría (buckets de morosidad, antigüedad de saldos): una tabla plana
 * perdería justamente lo que se va a mirar.
 *
 * @param {Object} cfg
 * @param {Object|string} [cfg.compania] {nombre, direccion, rnc, telefono} o el
 *   nombre solo; si falta, usa el nombre del negocio guardado en el login.
 * @param {string}  cfg.titulo       Título del reporte.
 * @param {string}  [cfg.subtitulo]  Rango/filtros.
 * @param {Array}   cfg.cabeceras    Encabezados de columnas (iguales en todos
 *   los grupos).
 * @param {Array}   cfg.grupos       [{titulo, resumen, filas, totales}] —
 *   `resumen` se pinta a la derecha del título del grupo; `totales` es la fila
 *   de subtotal, opcional. Los grupos sin filas se omiten.
 * @param {Array}   [cfg.totales]    Fila de total general.
 * @param {Array}   [cfg.totalesextra] Filas de total adicionales bajo la
 *   general, con el mismo formato (p.ej. entradas, salidas y neto).
 * @param {Array}   [cfg.numericas]  Índices de columnas alineadas a la derecha.
 * @param {string}  [cfg.pagina]     Tamaño de página ("letter", "letter landscape"…).
 */
export function imprimirListadoAgrupado({
  compania,
  titulo,
  subtitulo,
  cabeceras,
  grupos,
  totales,
  totalesextra = [],
  numericas = [],
  pagina = "letter",
}) {
  const comp = companiaListado(compania);
  const num = new Set(numericas);
  const celdas = (fila, tag) =>
    fila.map((v, i) => `<${tag}${num.has(i) ? ' class="num"' : ""}>${escHtml(v)}</${tag}>`).join("");
  const th = celdas(cabeceras, "th");

  const conFilas = (grupos || []).filter((g) => g && g.filas && g.filas.length);
  const registros = conFilas.reduce((acc, g) => acc + g.filas.length, 0);

  // break-inside: avoid mantiene junto cada grupo mientras quepa en la hoja;
  // los grupos largos igual se parten en vez de dejar una página en blanco.
  const cuerpo = conFilas
    .map(
      (g) => `
      <div class="grupo">
        <div class="grupotit">
          <span>${escHtml(g.titulo)}</span>
          ${g.resumen ? `<span class="resumen">${escHtml(g.resumen)}</span>` : ""}
        </div>
        <table class="lineas">
          <tr>${th}</tr>
          ${g.filas.map((f) => `<tr>${celdas(f, "td")}</tr>`).join("")}
          ${g.totales ? `<tr class="tot">${celdas(g.totales, "td")}</tr>` : ""}
        </table>
      </div>`
    )
    .join("");

  const filasTotal = [...(totales ? [totales] : []), ...(totalesextra || [])];
  const totalGeneral = filasTotal.length
    ? `<table class="lineas general">${filasTotal
        .map((t) => `<tr class="tot">${celdas(t, "td")}</tr>`)
        .join("")}</table>`
    : "";

  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>${escHtml(titulo)}</title>
    <style>${estilosListado(pagina)}
      .grupo { margin-top: 14px; break-inside: avoid; page-break-inside: avoid; }
      .grupotit { display: flex; justify-content: space-between; align-items: baseline; gap: 12px;
                  background: #eef3f6; border-left: 4px solid ${NAVY}; padding: 5px 8px;
                  font-size: 12px; font-weight: bold; color: ${NAVY}; }
      .grupotit .resumen { font-weight: normal; font-size: 11px; color: #4a5866; }
      .grupo table.lineas { margin-top: 0; }
      table.general { margin-top: 14px; }
    </style></head>
    <body>
      ${cabeceraListado(comp, titulo, subtitulo, registros)}
      ${cuerpo || '<p class="sub">Sin registros.</p>'}
      ${totalGeneral}
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
}

export default encabezadoEmpresaHtml;
